import {
  Card,
  CardTitle,
  CardHeader,
  CardDescription,
} from '@design-system/Card';

import { cn } from '@/utils/styles';
import { Skeleton } from '@design-system/Skeleton';

type ISectionData = {
  label: string;
  value?: string | number;
};

export interface SampleDetailsPageSectionProps
  extends Omit<React.ComponentProps<'div'>, 'title'> {
  isLoading?: boolean;
  data: ISectionData[];
  title: React.ReactNode;
  itemClassName?: string;
  'data-testid'?: string;
}

const renderSectionData = (
  sectionData: ISectionData[],
  isLoading?: boolean,
  baseTestId?: string,
) => {
  const renderedSectionData = sectionData.map((data) => {
    const itemTestId = baseTestId ? `${baseTestId}-item-${data.label.toLowerCase().replace(/\s+/g, '')}` : undefined;
    
    return (
      <li key={data.label} data-testid={itemTestId}>
        <Card data-testid={itemTestId ? `${itemTestId}-card` : undefined}>
          <CardHeader data-testid={itemTestId ? `${itemTestId}-header` : undefined}>
            <CardTitle className="typography-btn-large text-tertiary" data-testid={itemTestId ? `${itemTestId}-label` : undefined}>
              {data.label}
            </CardTitle>

            <CardDescription className="typography-btn-large text-primary" data-testid={itemTestId ? `${itemTestId}-value` : undefined}>
              {isLoading ? (
                <Skeleton className="w-full h-4 max-w-52" data-testid={itemTestId ? `${itemTestId}-skeleton` : undefined} />
              ) : (
                <span className="truncate">
                  <abbr title={String(data.value)} className="no-underline">
                    {data.value}
                  </abbr>
                </span>
              )}
            </CardDescription>
          </CardHeader>
        </Card>
      </li>
    );
  });

  return renderedSectionData;
};

export const SampleDetailsPageSection = ({
  data,
  title,
  isLoading,
  className,
  itemClassName,
  'data-testid': testId,
  ...rest
}: SampleDetailsPageSectionProps) => {
  return (
    <div {...rest} className={cn('flex flex-col', className)} data-testid={testId}>
      <span className="typography-h2 text-primary mb-6" data-testid={testId ? `${testId}-title` : undefined}>{title}</span>

      <ul className={cn("flex", "flex-col", "gap-2", itemClassName)} data-testid={testId ? `${testId}-list` : undefined}>
        {renderSectionData(data, isLoading, testId)}
      </ul>
    </div>
  );
};
